import Icon from '@Components/Icons/Icon/Icon'
import Skeleton from '@Components/Utilities/Skeleton/Skeleton'
import UnitTypeColors from '@Constants/UnitTypeColors'
import { Box, FlexLayout } from '@eduact/ed-system'
import { ContentConfig } from '@eduact/utils'
import { useGetLearningTimeQuery } from '@Features/Dashboard/Classrooms/Components/Tabs/SingleClassroomOverview/SingleClassroomSlice/SingleClassroomSlice'
import { ResponsivePie } from '@nivo/pie'
import { formatDuration, NumberFormatter } from '@Utilities/Formatters'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const BREAKDOWN_TYPES: Array<keyof LearningTime> = ['video', 'document', 'test', 'webinar', 'webcontent', 'assignment']

type BreakdownNode = PieChartNode & { type: keyof LearningTime }

type Props = {
	/** user scope (dashboard overview) or classroom scope (single-classroom analytics) */
	scope: ClassroomId | UserId
	skip?: boolean
}

/**
 * The learning-time donut shared by the user dashboard and the single-classroom
 * analytics: canonical per-type palette, formatted TOTAL in the center, tooltip
 * with type + formatted time + percent of total, and a text empty state.
 */
const LearningTimeBreakdown: React.FC<Props> = ({ scope, skip }) => {
	const [t] = useTranslation('studentDashboard')
	const { data: learningData, isLoading } = useGetLearningTimeQuery(scope, { skip })

	const nodes = useMemo<BreakdownNode[] | undefined>(() => {
		if (!learningData) return
		return BREAKDOWN_TYPES.map(type => ({
			id: type,
			type,
			label: type,
			value: learningData.data[type] ?? 0,
			color: UnitTypeColors[type],
		}))
	}, [learningData])

	const total = useMemo(() => nodes?.reduce((sum, node) => sum + node.value, 0) ?? 0, [nodes])

	if (isLoading) {
		return <Skeleton isLoading width="100%" height="180px" borderRadius="10px" />
	}

	if (!nodes || total <= 0) {
		return <EmptyText>{t('no_learning_time')}</EmptyText>
	}

	// custom layer: the formatted total inside the donut hole
	const CenteredTotal = ({ centerX, centerY }: { centerX: number; centerY: number }) => (
		<CenterText x={centerX} y={centerY} textAnchor="middle" dominantBaseline="central">
			{formatDuration(total)}
		</CenterText>
	)

	return (
		<ChartRow flexDirection="row" alignItems="center">
			<Box height={'100%'} width="100%">
				<ResponsivePie
					data={nodes}
					innerRadius={0.7}
					padAngle={0.7}
					margin={{ left: 20, right: 20 }}
					activeOuterRadiusOffset={8}
					colors={{ datum: 'data.color' }}
					enableArcLinkLabels={false}
					enableArcLabels={false}
					isInteractive
					fit
					animate={false}
					motionConfig="slow"
					layers={['arcs', CenteredTotal]}
					tooltip={({ datum }) => {
						const node = datum.data as BreakdownNode
						const percent = Math.round((node.value / total) * 100)
						return (
							<SliceContainer>
								<SliceItemWrapper>
									<Icon>{ContentConfig[node.type]?.mdIcon}</Icon>
									<span>{t(`unit_types.${node.type}`, { defaultValue: node.type })}</span>
								</SliceItemWrapper>
								<SliceItemWrapper>
									<span>
										{t('time')}: {formatDuration(node.value)} ({NumberFormatter(percent)}%)
									</span>
								</SliceItemWrapper>
							</SliceContainer>
						)
					}}
				/>
			</Box>
			<LegendWrapper>
				{nodes.map(node => (
					<FlexLayout key={node.type} gridGap="0.5rem" alignItems="center" height="1rem">
						<Dot color={node.color}>•</Dot>
						<LegendText color={node.color}>{t(`unit_types.${node.type}`, { defaultValue: node.type })}</LegendText>
					</FlexLayout>
				))}
			</LegendWrapper>
		</ChartRow>
	)
}

export default LearningTimeBreakdown

const ChartRow = styled(FlexLayout)`
	width: 100%;
	height: 100%;
	min-height: 0;
`
const EmptyText = styled.p`
	width: 100%;
	margin: auto;
	text-align: center;
	font: var(--fw-medium) 14px var(--font-sans);
	color: var(--text-secondary);
`
const CenterText = styled.text`
	font: var(--fw-bold) 16px var(--font-sans);
	fill: var(--text-primary);
`
const LegendWrapper = styled(FlexLayout)`
	flex-direction: column;
	flex: none;
`
const Dot = styled.span<{ color: string | undefined }>`
	font-size: 2.5rem;
	color: ${({ color }) => color};
`
const LegendText = styled.span<{ color: string | undefined }>`
	color: ${({ color }) => color};
	font-size: 0.7rem;
	font-weight: 600;
	margin-top: 5px;
`
const SliceContainer = styled(FlexLayout)`
	border: 2px solid ${props => props.theme.colors.primary};
	background: ${props => props.theme.colors.light};
	padding: 8px;
	border-radius: 10px;
	display: grid;
	row-gap: 10px;
`
const SliceItemWrapper = styled(FlexLayout)`
	font-size: 0.9rem;
	font-weight: 600;
	gap: 0.2rem;
	color: ${props => props.theme.colors.spanishGray};
	text-transform: capitalize;
	display: flex;
	align-items: center;
	column-gap: 5px;
	min-width: 7rem;
`
